/* eslint-disable @typescript-eslint/no-explicit-any */
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { call, delay, put, takeLatest } from 'redux-saga/effects';

import apiNew from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import {
 AccessGroupsDTO,
 CreateUpdateUsersDTO,
 FilterDTO,
 GroupDTO,
 StructureType,
 UsersDTO,
} from './usersTypes';
import * as usersSlice from './usersSlice';
import * as commonSagas from '../_common/commonSagas';

function* usersGetSelfSaga(props: PayloadAction<{ fields?: string }>) {
 try {
  const params = props.payload.fields
   ? `?fields=${encodeURIComponent(props.payload.fields)}`
   : '';
  const response: AxiosResponse<UsersDTO> = yield call(
   apiNew.get,
   `/Users/v1/self${params}`,
  );
  yield put(usersSlice.usersGetSelfSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetSelfFailure);
 }
}

function* usersCreateUserSaga(props: PayloadAction<CreateUpdateUsersDTO>) {
 try {
  const response: AxiosResponse<UsersDTO> = yield call(
   apiNew.post,
   '/Users/v1/create',
   props.payload,
  );
  yield put(usersSlice.usersCreateUserSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersCreateUserFailure);
 }
}

function* usersUpdateSelfSaga(props: PayloadAction<CreateUpdateUsersDTO>) {
 try {
  const response: AxiosResponse<UsersDTO> = yield call(
   apiNew.put,
   '/Users/v1/self/update',
   props.payload,
  );
  yield put(usersSlice.usersUpdateSelfSuccess(response.data));
  yield delay(0);
  yield put(usersSlice.usersUpdateSelfFinish());
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersUpdateSelfFailure);
 }
}

function* usersGetUserGroupsSaga(props: PayloadAction<FilterDTO>) {
 try {
  const response: AxiosResponse<GroupDTO[]> = yield call(
   apiNew.post,
   '/Users/v1/groups',
   props.payload,
  );
  yield put(usersSlice.usersGetUserGroupsSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetUserGroupsFailure);
 }
}

function* usersGetUserGroupSaga(
 props: PayloadAction<{ id: number; fields?: string }>,
) {
 try {
  const params = props.payload.fields
   ? `?fields=${encodeURIComponent(props.payload.fields)}`
   : '';
  const response: AxiosResponse<GroupDTO> = yield call(
   apiNew.get,
   `/Users/v1/groups/${props.payload.id}${params}`,
  );
  yield put(usersSlice.usersGetUserGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetUserGroupFailure);
 }
}

function* usersCreateUserGroupSaga(props: PayloadAction<any>) {
 try {
  const response: AxiosResponse<GroupDTO> = yield call(
   apiNew.post,
   '/Users/v1/groups/create',
   props.payload,
  );
  yield put(usersSlice.usersCreateUserGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersCreateUserGroupFailure);
 }
}

function* usersUpdateUserGroupSaga(
 props: PayloadAction<{ id: number; data: any }>,
) {
 try {
  const response: AxiosResponse<GroupDTO> = yield call(
   apiNew.put,
   `/Users/v1/groups/update/${props.payload.id}`,
   props.payload.data,
  );
  yield put(usersSlice.usersUpdateUserGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersUpdateUserGroupFailure);
 }
}

function* usersGetStructureSaga(props: PayloadAction<{ lang?: string }>) {
 try {
  const params = props.payload.lang
   ? `?lang=${encodeURIComponent(props.payload.lang)}`
   : '';
  const response: AxiosResponse<StructureType[]> = yield call(
   apiNew.get,
   `/Users/v1/structure${params}`,
  );
  yield put(usersSlice.usersGetStructureSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetStructureFailure);
 }
}

function* usersGetUserAccessGroupsSaga(
 props: PayloadAction<{ userId: string }>,
) {
 try {
  const response: AxiosResponse<AccessGroupsDTO[]> = yield call(
   apiNew.get,
   `/Users/v1/${props.payload.userId}/accessgroups`,
  );
  yield put(usersSlice.usersGetUserAccessGroupsSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetUserAccessGroupsFailure);
 }
}

function* usersGetSelfAccessGroupSaga(
 props: PayloadAction<{ accessgroupId: string }>,
) {
 try {
  const response: AxiosResponse<AccessGroupsDTO[]> = yield call(
   apiNew.get,
   `/Users/v1/self/accessgroups/${props.payload.accessgroupId}`,
  );
  yield put(usersSlice.usersGetSelfAccessGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetSelfAccessGroupFailure);
 }
}

function* usersGetSelfAccessGroupsSaga() {
 try {
  const response: AxiosResponse<AccessGroupsDTO[]> = yield call(
   apiNew.get,
   '/Users/v1/self/accessgroups',
  );
  yield put(usersSlice.usersGetSelfAccessGroupsSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetSelfAccessGroupsFailure);
 }
}

function* usersGenerateApiKeySaga(props: PayloadAction<{ userId: string }>) {
 try {
  const response: AxiosResponse<string | { ApiKey?: string }> = yield call(
   apiNew.get,
   `/Users/v1/${props.payload.userId}/generateapikey`,
  );
  const apiKey =
   typeof response.data === 'string'
    ? response.data
    : (response.data?.ApiKey ?? '');
  yield put(usersSlice.usersGenerateApiKeySuccess(apiKey));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGenerateApiKeyFailure);
 }
}

function* usersGetDatabasesSaga() {
 try {
  const response: AxiosResponse<UsersDTO['Databases']> = yield call(
   apiNew.get,
   '/Users/v1/databases',
  );
  yield put(usersSlice.usersGetDatabasesSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersGetDatabasesFailure);
 }
}

function* usersAttachDatabaseSaga(props: PayloadAction<any>) {
 try {
  const response: AxiosResponse<UsersDTO['Databases']> = yield call(
   apiNew.post,
   '/Users/v1/databases/attach',
   props.payload,
  );
  yield put(usersSlice.usersAttachDatabaseSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersAttachDatabaseFailure);
 }
}

function* usersDetachDatabaseSaga(props: PayloadAction<any>) {
 try {
  const response: AxiosResponse<UsersDTO['Databases']> = yield call(
   apiNew.del,
   '/Users/v1/databases/detach',
   props.payload,
  );
  yield put(usersSlice.usersDetachDatabaseSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, usersSlice.usersDetachDatabaseFailure);
 }
}

export function* watchUsersSaga() {
 yield takeLatest(usersSlice.getTableStart.type, commonSagas.getTable);
 yield takeLatest(usersSlice.getItemStart.type, commonSagas.getItem);
 yield takeLatest(usersSlice.createItemStart.type, commonSagas.createItem);
 yield takeLatest(usersSlice.updateItemStart.type, commonSagas.updateItem);
 yield takeLatest(usersSlice.deleteItemsStart.type, commonSagas.deleteItems);
 yield takeLatest(usersSlice.deleteItemStart.type, commonSagas.deleteItem);
 yield takeLatest(
  usersSlice.getTableStructureStart.type,
  commonSagas.getTableStructure,
 );
 yield takeLatest(
  usersSlice.getItemDocumentsStart.type,
  commonSagas.getItemDocuments,
 );
 yield takeLatest(
  usersSlice.uploadItemDocumentsStart.type,
  commonSagas.uploadItemDocuments,
 );
 yield takeLatest(
  usersSlice.deleteItemDocumentStart.type,
  commonSagas.deleteItemDocument,
 );
 yield takeLatest(
  usersSlice.getTableExcelStart.type,
  commonSagas.getTableExcel,
 );
 yield takeLatest(usersSlice.usersGetSelfStart.type, usersGetSelfSaga);
 yield takeLatest(usersSlice.usersCreateUserStart.type, usersCreateUserSaga);
 yield takeLatest(usersSlice.usersUpdateSelfStart.type, usersUpdateSelfSaga);
 yield takeLatest(
  usersSlice.usersGetUserGroupsStart.type,
  usersGetUserGroupsSaga,
 );
 yield takeLatest(
  usersSlice.usersGetUserGroupStart.type,
  usersGetUserGroupSaga,
 );
 yield takeLatest(
  usersSlice.usersCreateUserGroupStart.type,
  usersCreateUserGroupSaga,
 );
 yield takeLatest(
  usersSlice.usersUpdateUserGroupStart.type,
  usersUpdateUserGroupSaga,
 );
 yield takeLatest(
  usersSlice.usersGetStructureStart.type,
  usersGetStructureSaga,
 );
 yield takeLatest(
  usersSlice.usersGetUserAccessGroupsStart.type,
  usersGetUserAccessGroupsSaga,
 );
 yield takeLatest(
  usersSlice.usersGetSelfAccessGroupStart.type,
  usersGetSelfAccessGroupSaga,
 );
 yield takeLatest(
  usersSlice.usersGetSelfAccessGroupsStart.type,
  usersGetSelfAccessGroupsSaga,
 );
 yield takeLatest(
  usersSlice.usersGenerateApiKeyStart.type,
  usersGenerateApiKeySaga,
 );
 yield takeLatest(
  usersSlice.usersGetDatabasesStart.type,
  usersGetDatabasesSaga,
 );
 yield takeLatest(
  usersSlice.usersAttachDatabaseStart.type,
  usersAttachDatabaseSaga,
 );
 yield takeLatest(
  usersSlice.usersDetachDatabaseStart.type,
  usersDetachDatabaseSaga,
 );
}
